import * as Sentry from '@sentry/vue';
import { configureHttp } from '@/lib/http';
import { showAlert } from '@/lib/helpers';
import { readUsername } from '@/lib/session';

/**
 * Reads the CSRF token from local storage.
 *
 * The previous implementation read `JSON.parse(...).token` without a guard and
 * threw a TypeError whenever no user was signed in. That error was never fixed,
 * only silenced through `ignoredMessages` in plugins/sentry.js.
 *
 * @returns {string|null} The token, or null when the user is signed out.
 */
const readToken = () => {
  try {
    const raw = localStorage.getItem('logged_in_qram');
    if (!raw) return null;
    return JSON.parse(raw)?.token || null;
  } catch (error) {
    return null;
  }
};

/**
 * Resolves the language used for the `Accept-Language` header.
 *
 * The `language_qram` key is never written anywhere in the application, so it
 * always reads back as null and this function effectively returns Indonesian.
 * It still reads the key so that a future language switcher only has to write
 * it, without touching the HTTP layer.
 *
 * @returns {string} Either `'id'` or `'en'`.
 */
const readLanguage = () =>
  localStorage.getItem('language_qram') === 'en' ? 'en' : 'id';

/**
 * Connects the HTTP layer to the application store.
 *
 * Called from main.js once the store exists. The store arrives as a parameter
 * rather than an import, which keeps the store and the HTTP layer from forming
 * a circular dependency.
 *
 * @param {Object} store - The Vuex store instance.
 * @returns {void}
 */
export default function initHttp(store) {
  /**
   * Several requests can be in flight at once and fail with 401 together.
   * Without this flag each failure would trigger its own sign-out and its own
   * alert.
   *
   * @type {boolean}
   */
  let signingOut = false;

  configureHttp({
    getToken: readToken,
    getLanguage: readLanguage,
    getUsername: readUsername,

    /**
     * Contract version requested by the client. Turn this on once the version
     * marker has been agreed with the backend.
     */
    apiVersion: null,

    onUnauthenticated: (error) => {
      if (signingOut) return;
      signingOut = true;

      showAlert(store, error.message, 'mdi-alert', 'red');

      Promise.resolve(store.dispatch('logout')).finally(() => {
        signingOut = false;
      });
    },

    /**
     * The contract treats 403 as a valid identity without permission, so the
     * session is left alone. This differs from the old lib/fetcher.js, which
     * always signed the user out on 403.
     */
    onForbidden: (error) => {
      showAlert(store, error.message, 'mdi-alert', 'red');
    },

    /**
     * Matches the `beforeSend` filter in plugins/sentry.js, which only lets
     * server failures through.
     */
    onError: (error) => {
      if (error.isServer) {
        Sentry.captureException(error);
      }
    },
  });
}
